import auth0, { CheckSessionOptions } from "auth0-js";
import React from "react";
import ReactDOM from "react-dom";
import { AUTH_CONFIG } from "../authentication/auth0-variables";
import HandleAuth from './HandleAuth';

export interface AuthenticationData {
  created_at: string,
  email_verified: string,
  firstname: string,
  lastname: string,
  phone_number: string,
  company: string,
  session_expires_at: string,
  access_token: string,
  email?: string,
  expires_at: string,
  id_token: string,
  refresh_token: string,
  name: string,
  nickname: string,
  picture: string,
  updated_at: Date,
  user_id: string,
}

export interface AuthInterface {
  handleAuthentication: () => void;
  refreshSession: () => void;
  login: () => void;
  logout: () => void;
  isAuthenticated: () => boolean;
  authVariables: AuthenticationData|null;
  onSessionUpdate: () => void
}

export interface MGMT_API_Response {
  access_token: string;
  scope: string;
  expires_in: number;
  token_type: string;
}

export interface Auth0Roles {
  id: string;
  name: string;
  description: string;
}

export class Auth implements AuthInterface {
  public authVariables: AuthenticationData|null = null;
  public onSessionUpdate: () => void = () => null;
  tokenRenewalTimeout: any;
  // not yet used:
  // cb: ((error: any, result?: any) => void) | undefined;
  // handleAuthRoute: any;
  auth = new auth0.WebAuth({
    domain: AUTH_CONFIG.domain,
    clientID: AUTH_CONFIG.clientId,
    redirectUri: AUTH_CONFIG.callbackUrl,
    responseType: 'token id_token',
    scope: 'openid profile offline_access'
  });

  public mgmt_token: MGMT_API_Response|null = null;
  public userRoles: Auth0Roles[] = [];

  login() {
    this.auth.authorize();
  }

  handleAuthentication() {
    this.auth.parseHash((err, authResult) => {
      if (authResult && authResult.accessToken && authResult.idToken) {
        this.setSession(authResult);
      } else if (err) {
        console.log(err);
        if (err.errorDescription === 'NEW-SIGN-UP-PLEASE-VERIFY' || err.errorDescription === 'Please verify your email before logging in.'){
          ReactDOM.render(<HandleAuth errorMessage={'New User Sign Up - A verification email has been sent to you. Please verify your email before logging in.'} errorCode={err.code!}/>, document.getElementById("root"));
        } else {
          alert(`Error: ${err.error}. Check the console for further details.`);
        }
      }
    });
  }

  public refreshSession = () => {
      const options: CheckSessionOptions = {};
      this.auth.checkSession(options,
        (err: auth0.Auth0Error|null, result: auth0.Auth0DecodedHash) => {
          if (err) {
            if (err.code?.includes("login_required")){
              // Please comment the line below after the dialog box implementation is complete.
              //ReactDOM.render(<HandleAuth errorMessage={err.error_description!} errorCode={err.code}/>, document.getElementById("root"));
            }
            else if (err.error_description === 'NEW-SIGN-UP-PLEASE-VERIFY' || err.error_description === 'Please verify your email before logging in.'){
              ReactDOM.render(<HandleAuth errorMessage={'New User Sign Up - A verification email has been sent to you. Please verify your email before logging in.'} errorCode={err.code!}/>, document.getElementById("root"));
            }
            else{
              ReactDOM.render(<HandleAuth errorMessage={'Could not get a new token '+ err.error_description!} errorCode={err.code!}/>, document.getElementById("root"));
            }
          } else {
            this.setSession(result);
          }
        }
      );
  }

  setSession(authResult: auth0.Auth0DecodedHash) {
    // Set the time that the access token will expire at
    if (!authResult.accessToken) {
      return;
    }
    
    let sessionExpiresAt = '0';
    let expiresAt = '0';
    let isExpired = true; 
    if (authResult.idTokenPayload.exp) {
      isExpired = this.isAuthenticationExpired(authResult.idTokenPayload.exp);
      expiresAt = JSON.stringify(authResult.idTokenPayload.exp);
    }
    if (authResult.expiresIn) {
      isExpired = this.isAuthenticationExpired(authResult.expiresIn);
      sessionExpiresAt = JSON.stringify(
        (authResult.expiresIn * 1000) + new Date().getTime()
      );

    }

    if (isExpired) return;

    this.auth.client.userInfo(authResult.accessToken, (err, user) => {      

      if (err) {
        console.error('userInfo Error:', err);
        return;
      }

      const accessToken = authResult.accessToken ? authResult.accessToken : '';
      const idToken = authResult.idToken ? authResult.idToken : '';
      const refreshToken = authResult.refreshToken ? authResult.refreshToken : '';

      this.authVariables = {
        id_token: idToken,
        refresh_token: refreshToken,
        access_token: accessToken,
        email: user.email,
        expires_at: expiresAt,
        name: user.name,
        nickname: user.nickname,
        picture: user.picture,
        updated_at: new Date(Date.parse(user.updated_at)),
        user_id: authResult.idTokenPayload.sub,
        email_verified: authResult.idTokenPayload.email_verified,
        firstname: authResult.idTokenPayload.user_metadata.given_name,
        lastname: authResult.idTokenPayload.user_metadata.family_name,
        phone_number: authResult.idTokenPayload.user_metadata.phone_number,
        company: authResult.idTokenPayload.user_metadata.company,
        created_at: authResult.idTokenPayload.created_at,
        session_expires_at: sessionExpiresAt,
      };
      this.onSessionUpdate();
      this.scheduleRenewal();

  });
  }

  download() {
    let token_data = `{"id": "${this.authVariables?.id_token}", "refresh": "${this.authVariables?.refresh_token}", "expiry": "${this.authVariables?.expires_at}"}`;
    const a = window.document.createElement('a');
    a.setAttribute('href', `data:application/json;charset=utf-8,${encodeURIComponent(token_data)}`);
    a.style.display = 'none';
    a.setAttribute('download', 'DeltaJSON_Auth.json');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  getAccessToken() {
    const accessToken = this.authVariables?.access_token;
    if (!accessToken) {
      throw new Error('No access token found');
    }
    return accessToken;
  }

  logout() {
    // logout use form the auth0
    window.location.href = `https://${AUTH_CONFIG.domain}/v2/logout?returnTo=${AUTH_CONFIG.redirectUrl}/&client_id=${AUTH_CONFIG.clientId}`;
  }

  isAuthenticated() {
    // Check whether the current time is past the
    // access token's expiry time
    let time = this.authVariables?.session_expires_at || '';
    let sessionExpiresAt: number = (time.length > 0) ? JSON.parse(time) : new Date().getTime()
    return new Date().getTime() < sessionExpiresAt;
  }

  isAuthenticationExpired(expiresIn: number) {
    const adjustedExpiresIn = (expiresIn * 1000) + new Date().getTime();
    return !(new Date().getTime() < adjustedExpiresIn);
  }

  renewToken() {
    this.auth.checkSession({},
      (err: auth0.Auth0Error|null, result: auth0.Auth0DecodedHash) => {
        if (err) {
          ReactDOM.render(<HandleAuth errorMessage={'Could not get a new token '+ err.error_description!} errorCode={err.code!}/>, document.getElementById("root"));
        } else {
          this.setSession(result);
          alert(`Successfully renewed auth!`);
        }
      }
    );
  }

  scheduleRenewal() {
    const sessionExpiresAt: number = JSON.parse(this.authVariables?.session_expires_at || '');
    const delay = sessionExpiresAt - Date.now();
    if (delay > 0) {
      this.tokenRenewalTimeout = setTimeout(() => {
        this.renewToken();
      }, delay);
    }
  }
}

export const AuthContext = React.createContext(new Auth());